// 시뮬레이터 등록 및 관리 시스템
//
// ⚠️ 시뮬레이터는 **필요할 때 받는다**(React.lazy + dynamic import).
//   정적으로 import 하면 스물아홉 개가 전부 첫 번들에 들어간다 — 라이선스 코드를
//   입력하는 첫 화면 하나 보려고 시뮬레이터 전부와 직무영어 그림 데이터(2.2 MB)까지
//   받게 되고, 실제로 그래서 첫 화면이 느렸다(2026-09: 번들 10.9 MB / gzip 3.5 MB).
//   목록·이름·카테고리 같은 **메타데이터는 그대로 즉시 쓸 수 있다** — 무거운 건 화면뿐이다.
//   ⚠️ 그래서 시뮬 컴포넌트를 그리는 자리는 반드시 <Suspense> 안이어야 한다.
import { lazy } from 'react';
const VacuumSimulator = lazy(() => import('../simulators/VacuumSimulator'));
const CleaningSimulator = lazy(() => import('../simulators/CleaningSimulator'));
const OxidationSimulator = lazy(() => import('../simulators/Oxidation'));
const LithographySimulator = lazy(() => import('../simulators/LithographySimulator'));
const PlasmaSimulator = lazy(() => import('../simulators/PlasmaSimulator'));
const PlasmaSimulatorI = lazy(() => import('../simulators/PlasmaSimulatorI'));
const PlasmaSimulatorII = lazy(() => import('../simulators/PlasmaSimulatorII'));
const PlasmaSimulatorIII = lazy(() => import('../simulators/PlasmaSimulatorIII'));
// semifabai.com 원리 화면 이식본(서고 플라즈마 심화·식각) — 엔진은 src/physics 의 ccpSelfBias·iedfSheath·ionRadicalSynergy.
const CcpSelfBiasSim = lazy(() => import('../simulators/CcpSelfBiasSim'));
const IedfSheathSim = lazy(() => import('../simulators/IedfSheathSim'));
const IonRadicalSynergySim = lazy(() => import('../simulators/IonRadicalSynergySim'));
// 서고 전용(본진·데모에 없음) — 정식 버전의 '공정 연결'·'측정' 층.
const IedfEtchSim = lazy(() => import('../simulators/IedfEtchSim'));
const RfeaSim = lazy(() => import('../simulators/RfeaSim'));
const QcmSynergySim = lazy(() => import('../simulators/QcmSynergySim'));
const AleSim = lazy(() => import('../simulators/AleSim'));
const CcpEtchSim = lazy(() => import('../simulators/CcpEtchSim'));
// 데모(demo.semifabai.com) 시스템 구조 화면 이식본 — 본체 src/simulators/plasma/structure, 엔진 physics/plasmaStructure·plasmaII.
const CcpStructureSim = lazy(() => import('../simulators/CcpStructureSim'));
// 서고 전용 — 데모 이온주입(도핑) 화면의 개념을 넓힌 '라인'(빔라인→주입→어닐→측정→미션). 엔진 physics/implantLine.
const ImplantLineSim = lazy(() => import('../simulators/ImplantLineSim'));
// 서고 전용(본진·데모에 없음) — 나노·신소재 선반. 물리 시뮬이 아니라 역사 탐방·구조 뷰어라 규칙 9 의 세 층 묶음 대상이 아니다.
const NobelNanoTour = lazy(() => import('../simulators/nano/NobelNanoTour'));
const MofReticularViewer = lazy(() => import('../simulators/nano/MofReticularViewer'));
const CarbonAllotropeGallery = lazy(() => import('../simulators/nano/CarbonAllotropeGallery'));
const CntRollupSim = lazy(() => import('../simulators/nano/CntRollupSim'));
const SurfaceAtomsSim = lazy(() => import('../simulators/nano/SurfaceAtomsSim'));
const NucleationSim = lazy(() => import('../simulators/nano/NucleationSim'));
const DlvoSim = lazy(() => import('../simulators/nano/DlvoSim'));
const RfLcSim = lazy(() => import('../simulators/plasma/RfLcSim'));
// 진공 리크 진단(진공과 플라즈마 4강) — 서고가 원본, 데모 vacuumLeak 카드가 사본(상황 5개 중 3개 잠금). 모델 physics/vacuumLeak(작은 모듈).
const VacuumLeakSim = lazy(() => import('../simulators/VacuumLeakSim'));
// 후공정 실습 셋(데모 ?sim=metallization-eds-packaging 의 EDS·패키징·본딩 탭 맨 위 실습의 이식본) — 모델 physics/edsYield · hbm · bondingIO.
const EdsYieldLab = lazy(() => import('../simulators/postfab/EdsYieldLab'));
const HbmLab = lazy(() => import('../simulators/postfab/HbmLab'));
const BondingLab = lazy(() => import('../simulators/postfab/BondingLab'));
// 다마신 실습(데모 ?sim=metallization-eds-packaging 다마신 탭 맨 위 실습의 이식본) — 모델 physics/damascene.
const DamasceneLab = lazy(() => import('../simulators/damascene/DamasceneLab'));
// 확산 실습 '두 단계로 접합 만들기'(데모 ?sim=implantation 확산 탭 맨 위 실습의 이식본) — 모델 physics/twoStepDiffusion.
const DiffusionTwoStepLab = lazy(() => import('../simulators/diffusion/DiffusionTwoStepLab'));
const OledSim = lazy(() => import('../simulators/nano/OledSim'));
const LcdPixelSim = lazy(() => import('../simulators/nano/LcdPixelSim'));
const VdwStackSim = lazy(() => import('../simulators/nano/VdwStackSim'));
const MoireSim = lazy(() => import('../simulators/nano/MoireSim'));
const LedColorSim = lazy(() => import('../simulators/nano/LedColorSim'));
const MocvdGrowthSim = lazy(() => import('../simulators/nano/MocvdGrowthSim'));
const QdSizeSim = lazy(() => import('../simulators/nano/QdSizeSim'));
const PerovskiteSim = lazy(() => import('../simulators/nano/PerovskiteSim'));
const SensorCalibSim = lazy(() => import('../simulators/sensor/SensorCalibSim'));
const SensorResponseSim = lazy(() => import('../simulators/sensor/SensorResponseSim'));
const TempSensorCompareSim = lazy(() => import('../simulators/sensor/TempSensorCompareSim'));
const NtcSelfHeatSim = lazy(() => import('../simulators/sensor/NtcSelfHeatSim'));
const PhotoCutoffSim = lazy(() => import('../simulators/sensor/PhotoCutoffSim'));
const PhotodiodeSim = lazy(() => import('../simulators/sensor/PhotodiodeSim'));
const UltrasonicRangeSim = lazy(() => import('../simulators/sensor/UltrasonicRangeSim'));
const HallSensorSim = lazy(() => import('../simulators/sensor/HallSensorSim'));
const FlowMetersSim = lazy(() => import('../simulators/sensor/FlowMetersSim'));
const ThermalMfcSim = lazy(() => import('../simulators/sensor/ThermalMfcSim'));
const MemsAccelSim = lazy(() => import('../simulators/sensor/MemsAccelSim'));
const MemsPressureSim = lazy(() => import('../simulators/sensor/MemsPressureSim'));
const GasMosSim = lazy(() => import('../simulators/sensor/GasMosSim'));
const ZirconiaO2Sim = lazy(() => import('../simulators/sensor/ZirconiaO2Sim'));
const HumidityAirSim = lazy(() => import('../simulators/sensor/HumidityAirSim'));
const HumidityCalSim = lazy(() => import('../simulators/sensor/HumidityCalSim'));
const VIProbeSim = lazy(() => import('../simulators/sensor/VIProbeSim'));
const IcpTransformerSim = lazy(() => import('../simulators/sensor/IcpTransformerSim'));
const FermiLevelSim = lazy(() => import('../simulators/semi/FermiLevelSim'));
const PnJunctionSim = lazy(() => import('../simulators/semi/PnJunctionSim'));
const MetaRefractionSim = lazy(() => import('../simulators/nano/MetaRefractionSim'));
const EmiShieldSim = lazy(() => import('../simulators/nano/EmiShieldSim'));
const SuperconductorSim = lazy(() => import('../simulators/nano/SuperconductorSim'));
const PowerChipSim = lazy(() => import('../simulators/nano/PowerChipSim'));
const HemtSim = lazy(() => import('../simulators/nano/HemtSim'));
const TunnelSim = lazy(() => import('../simulators/nano/TunnelSim'));
const QubitSim = lazy(() => import('../simulators/nano/QubitSim'));
const HighKSim = lazy(() => import('../simulators/nano/HighKSim'));
const LowKSim = lazy(() => import('../simulators/nano/LowKSim'));
const EtchChamberTrainer = lazy(() => import('../simulators/EtchChamberTrainer'));
const GasDeliveryHmi = lazy(() => import('../simulators/GasDeliveryHmi'));
const IcpStructureSim = lazy(() => import('../simulators/IcpStructureSim'));
const CcpQMeasureSim = lazy(() => import('../simulators/CcpQMeasureSim'));
const PaschenCurveIISimulator = lazy(() => import('../simulators/PaschenCurveIISimulator'));
const RFMatchPlasmaSimulator = lazy(() => import('../simulators/RFMatchPlasmaSimulator'));
const EtchingSimulator = lazy(() => import('../simulators/EtchingSimulator'));
const DopingProcessSimulator = lazy(() => import('../simulators/Dopingprocesssimulator'));
const DepositionSimulator = lazy(() => import('../simulators/DepositionSimulator'));
const MetallizationEDSPackagingSimulator = lazy(() => import('../simulators/MetallizationEDSPackagingSimulator'));
const ComprehensiveAssessmentSimulator = lazy(() => import('../simulators/ComprehensiveAssessmentSimulator'));
const ALDSimulator = lazy(() => import('../simulators/ALDSimulator'));
const PECVDSimulator = lazy(() => import('../simulators/PECVDSimulator'));
const LPCVDSimulator = lazy(() => import('../simulators/LPCVDSimulator'));
const RTASimulator = lazy(() => import('../simulators/RTASimulator'));
const SputteringSimulator = lazy(() => import('../simulators/SputteringSimulator'));
const EvaporatorSimulator = lazy(() => import('../simulators/EvaporatorSimulator'));
const NanoDeviceComparisonSimulator = lazy(() => import('../simulators/NanoDeviceComparisonSimulator'));
const SEMSimulator = lazy(() => import('../simulators/SEMSimulator'));
const RGASimulator = lazy(() => import('../simulators/RGASimulator'));
const OESSimulator = lazy(() => import('../simulators/OESSimulator'));
const XPSSimulator = lazy(() => import('../simulators/XPSSimulator'));
const MemoryIndustrySimulator = lazy(() => import('../simulators/MemoryIndustrySimulator'));
const DeviceArchitectureMapSimulator = lazy(() => import('../simulators/DeviceArchitectureMapSimulator'));
const FieldEnglishSimulator = lazy(() => import('../simulators/FieldEnglishSimulator'));
const WaferTransferSim = lazy(() => import('../simulators/WaferTransferSim'));
const JobDrillSim = lazy(() => import('../simulators/JobDrillSim'));

class SimulatorRegistry {
  constructor() {
    this.simulators = new Map();
    this.initializeSimulators();
  }

  // 시뮬레이터 초기화
  initializeSimulators() {
    // 진공 시뮬레이터 (현재 완성됨)
    this.register({
      id: 'vacuum',
      name: 'Vacuum 기초',
      icon: '⚡',
      description: '진공 펌핑 및 성능 분석',
      component: VacuumSimulator,
      available: true,
      category: 'basic',
      order: 1,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2025-01-01',
        author: 'Semiconductor Simulator Team',
        features: [
          '실시간 펌핑 시뮬레이션',
          '성능 특성 곡선 분석',
          '공정 압력 세팅 실험',
          'Conductance & 압력 관계',
          '배관 설계 최적화',
          '진공 기술 퀴즈'
        ]
      }
    });

    // 진공 리크 진단 — 베이스 압력 미달 다섯 상황을 Rate of Rise · He 리크 디텍터 · RGA 로 가르고 조치(채점).
    this.register({
      id: 'vacuum-leak',
      name: '진공 리크 진단',
      icon: '🔎',
      description: 'Rate of Rise · He 리크 디텍터 · RGA 로 실제/가상/투과 리크를 가르고 고친다',
      component: VacuumLeakSim,
      available: true,
      category: 'basic',
      order: 1.1,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-22', author: 'Semiconductor Simulator Team',
        features: ['상황 5개(실제·가상·투과·복합)', 'Rate of Rise 선형/포화', 'He 분사·후드 모드', 'RGA 28/32·m/z 18', 'Pirani·BAG·CDG 측정 영역', '조치 채점(정지 시간·헛수고)'] },
    });

    // 웨이퍼 세정 시뮬레이터 (완성됨)
    this.register({
      id: 'cleaning',
      name: '웨이퍼 세정',
      icon: '🧽',
      description: 'RCA 세정 및 표면 처리',
      component: CleaningSimulator,
      available: true,
      category: 'process',
      order: 2,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2025-01-23',
        author: 'Semiconductor Simulator Team',
        features: [
          '세정 공정 개요 및 전략',
          '습식 세정 실시간 시뮬레이션',
          '건식 세정 방법 비교',
          '초음파 세정 캐비테이션 효과',
          '웨이퍼 단면 제거 과정 시각화',
          '중급 수준 세정 평가 퀴즈'
        ]
      }
    });

    // Oxidation 시뮬레이터 (준비중)
    this.register({
      id: 'oxidation',
      name: 'Oxidation',
      icon: '🔥',
      description: '열산화 및 게이트 절연막',
      component: OxidationSimulator,
      available: true,
      category: 'process',
      order: 3,
      metadata: {
        version: '0.0.0',
        estimatedRelease: '2025-02-15',
        features: [
          '건식/습식 산화',
          'Deal-Grove 모델',
          '온도/시간 최적화',
          '막질 특성 분석'
        ]
      }
    });

    // Lithography 시뮬레이터 (완성됨) ← 업데이트
    this.register({
      id: 'lithograph',
      name: 'Lithography',
      icon: '💡',
      description: '포토리소그라피 공정 및 PR 코팅',
      component: LithographySimulator, // ← 컴포넌트 추가
      available: true, // ← available을 true로 변경
      category: 'process',
      order: 4,
      metadata: {
        version: '1.0.0', // ← 버전 업데이트
        lastUpdated: '2025-01-30', // ← 최신 업데이트 날짜
        author: 'Semiconductor Simulator Team',
        features: [
          '포토리소그라피 8단계 공정 상세 설명',
          'PR 코팅 Recipe 실시간 시뮬레이션',
          'DUV/EUV 노광 방식 비교 분석',
          'Stepper vs Scanner 시스템 비교',
          'PSM 마스크 기술 및 진화 과정',
          '공정 애니메이션 및 시각화',
          '난이도별 평가 퀴즈 (10문제)',
          'RPM 파라미터 최적화 실험'
        ]
      }
    });

    // Plasma Simulation I 시뮬레이터 (완성됨)
    this.register({
      id: 'plasma',
      name: 'Plasma Simulation I',
      icon: '⚡',
      description: '플라즈마 발생원리 및 공정 제어',
      component: PlasmaSimulator,
      available: true,
      category: 'process',
      order: 5,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2025-01-29',
        author: 'Semiconductor Simulator Team',
        features: [
          '플라즈마 기본 특성 이해',
          '발생원리 및 파셴커브',
          'RF 매칭 네트워크',
          'CCP 시스템 구조',
          '고급 주제 및 미래 기술'
        ]
      }
    });

    // Plasma Simulation I (개념 분리용) — initialTheme 으로 발생원리1/2·RF매칭을 개별 진입.
    //  (registry 'plasma' 는 PlasmaSimulator.js 로 initialTheme 미지원 → 개념 분리는 이 항목 사용)
    this.register({
      id: 'plasma-i',
      name: 'Plasma Simulation I (개념)',
      icon: '⚡',
      description: '플라즈마 발생원리1/2 · RF 매칭 (개념별 진입)',
      component: PlasmaSimulatorI,
      available: true,
      category: 'process',
      order: 5.1,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-07-22',
        author: 'Semiconductor Simulator Team',
        features: ['플라즈마 발생원리1', '발생원리2(파셴)', 'RF 및 매칭'],
      },
    });

    // 파셴커브 II — DC 방전 모드(글로우/비정상 글로우/아크/스트리머) 전이. 14일 과정·매트릭스에만 있던 것을 서고에서도 연다.
    //  NPC 가이드는 시뮬 HTML(public/dc-plasma-discharge.html) 안에 들어 있어 따로 붙이지 않는다.
    this.register({
      id: 'paschen-ii',
      name: '파셴커브 II',
      icon: '⚡',
      description: 'DC 방전 모드 — 글로우 · 비정상 글로우 · 아크 · 스트리머',
      component: PaschenCurveIISimulator,
      available: true,
      category: 'process',
      order: 5.2,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-09-29',
        author: 'Semiconductor Simulator Team',
        features: ['4종 가스별 Paschen 파라미터', '글로우/비정상 글로우/아크(전류로 판정)·스트리머(높은 pd) 전이', 'NPC 가이드'],
      },
    });

    // 매칭 상태와 플라즈마 — 정상/미매칭/고장(케이블·접지·증폭소자·전극 돌출부) 별로
    //  반사·매처 위치와 플라즈마 모양을 같이 본다. 물리는 physics/rfMatch.js.
    this.register({
      id: 'rf-lc-resonance',
      name: 'L · C · 공진 · Q — 매처는 공진을 맞춘다',
      icon: '〰️',
      description: 'L · C · R 과 코일의 기생 C 를 바꿔 리액턴스 · 공진 · Q · SRF 를 보고, 매처 C1 이 공진을 맞추는 자리임을 확인한다',
      component: RfLcSim,
      available: true,
      category: 'process',
      order: 5.25,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-18', author: 'Semiconductor Simulator Team',
        features: ['X_L · X_C', '공진 f₀', 'Q · 대역폭', '실물 코일 SRF', '매처 1.2 µH 와 같은 값'] },
    });
    // 플라즈마 ① 보강 — RF L·C·공진·Q(센서재료 13편). 엔진 src/physics/rfLC.js(서고 전용, 수는 rfMatch 와 같은 곳).
    this.register({
      id: 'rf-match-plasma',
      name: '매칭 상태와 플라즈마',
      icon: '📡',
      description: '매칭·고장 모드별 반사 전력과 플라즈마 모양',
      component: RFMatchPlasmaSimulator,
      available: true,
      category: 'process',
      order: 5.3,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-09-29',
        author: 'Semiconductor Simulator Team',
        features: ['자동/수동 매칭', '케이블·접지·증폭소자·전극 아킹 고장 모드', '반사·C1/C2 추세', 'NPC 가이드'],
      },
    });

    // Plasma Simulation II 시뮬레이터 (완성됨)
    this.register({
      id: 'plasma-ii',
      name: 'Plasma Simulation II',
      icon: '🔬',
      description: 'ICP 시스템 및 고급 식각 공정',
      component: PlasmaSimulatorII,
      available: true,
      category: 'process',
      order: 6,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2025-01-30',
        author: 'Semiconductor Simulator Team',
        features: [
          'ICP 유도결합 원리 4단계 프로세스',
          'Synergy Effect 실험 시뮬레이션',
          '식각 메커니즘 상세 분석',
          '산업별 플라즈마 응용 분야',
          '난이도별 개념 확인 퀴즈 (30문제)',
          '실시간 애니메이션 및 타이핑 효과'
        ]
      }
    });

    // Plasma Simulation III 시뮬레이터 (완성됨) — DC/RF/주파수/IEDF 심화
    this.register({
      id: 'plasma-iii',
      name: 'Plasma Simulation III',
      icon: '⚡',
      description: 'DC/RF 방전 원리, 주파수 효과, 이온에너지 분포(IEDF)',
      component: PlasmaSimulatorIII,
      available: true,
      category: 'process',
      order: 6.5,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-07-15',
        author: 'Semiconductor Simulator Team',
        features: [
          'DC 플라즈마 원리 (Cathode sheath / 2차 전자)',
          'RF 플라즈마 & Self-bias 형성',
          '주파수 효과 (저주파 vs 고주파)',
          '이온에너지 분포 함수 (IEDF)',
          '공정 플라즈마 심화 퀴즈'
        ]
      }
    });

    // 아래 셋은 기존 화면(plasma-iii 의 Self-bias·IEDF 탭, 식각 시뮬)을 대체하지 않고 '물리 모델판'으로 곁에 둔다.
    // 같은 주제라도 기존 것은 개념 연출, 이쪽은 식으로 계산한 수치 — 서고 목차에 나란히 건다.
    this.register({
      id: 'ccp-self-bias',
      name: 'CCP 자기 바이어스 (물리 모델)',
      icon: '⚡',
      description: '면적비 법칙·Child 시스로 V_dc·시스 두께를 계산하는 CCP 단면 시뮬',
      component: CcpSelfBiasSim,
      available: true,
      category: 'process',
      order: 6.6,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-06',
        author: 'Semiconductor Simulator Team',
        features: ['면적비 V̄₁/V̄₂=(A₂/A₁)^q', '파워·압력·주파수 → V_rf·밀도(경향)', 'Child 시스 두께·시스 안 충돌', '고정 축 RF 파형'],
      },
    });

    this.register({
      id: 'iedf-sheath',
      name: '이온 에너지 분포 IEDF (시스 모델)',
      icon: '📊',
      description: 'P_bias=I·V_dc, Child 시스, τ_ion/τ_rf 로 IEDF 를 계산하는 ICP+RF 바이어스 시뮬',
      component: IedfSheathSim,
      available: true,
      category: 'process',
      order: 6.7,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-06',
        author: 'Semiconductor Simulator Team',
        features: ['주파수 → 두 봉우리 간격 ΔE', 'ICP 파워 → 플럭스(그리고 V_dc 하락)', '바이어스 → 평균 에너지', '압력 → 전하 교환 저에너지 꼬리'],
      },
    });

    this.register({
      id: 'ion-radical-synergy',
      name: '이온·라디칼 시너지',
      icon: '⚛️',
      description: 'Coburn–Winters 실험(5·2·55 Å/min)을 재현하는 표면 덮임 모델 — 시너지와 이방성',
      component: IonRadicalSynergySim,
      available: true,
      category: 'process',
      order: 9.5,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-06',
        author: 'Semiconductor Simulator Team',
        features: ['라디칼만·이온만·둘 다 식각률', '표면 덮임 θ', '이온 에너지 문턱(√E 법칙)', '바닥/측벽 비 = 이방성'],
      },
    });

    // 서고 전용 — 본진 IEDF 화면의 다음 단계. 본진 /modules/iedf 아래 '정식 버전에는' 안내가 이 둘을 가리킨다
    // (src/data/fullVersionCatalog.js · 테스트가 대조).
    this.register({
      id: 'iedf-etch',
      name: 'IEDF → 식각 결과',
      icon: '🧪',
      description: '이온 에너지 분포를 표면 반응 모델에 넣어 바닥·측벽 식각률·이방성·문턱 위 이온 비율을 계산',
      component: IedfEtchSim,
      available: true,
      category: 'process',
      order: 9.6,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-06',
        author: 'Semiconductor Simulator Team',
        features: ['분포 평균 수율(√E − √E_th)', '바닥/측벽·이방성', '문턱 위 이온 비율', '평균 에너지 한 점 계산과 비교'],
      },
    });

    this.register({
      id: 'rfea',
      name: 'RFEA 측정 — IEDF 되찾기',
      icon: '📟',
      description: '지연 전위 분석기로 전압을 훑어 I(V)를 재고 −dI/dV 로 이온 에너지 분포를 복원',
      component: RfeaSim,
      available: true,
      category: 'process',
      order: 6.8,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-06',
        author: 'Semiconductor Simulator Team',
        features: ['전압 스윕 I(V)', '−dI/dV 복원', '스텝·평균·평활 트레이드오프', '정답 가리기/열기'],
      },
    });

    // 서고 전용 — 본진 /learn/ion-radical-synergy 의 다음 단계(C 측정해서 얻기 · D 현업 방식).
    this.register({
      id: 'qcm-synergy',
      name: 'QCM 으로 재는 Coburn–Winters 실험',
      icon: '⚖️',
      description: '수정 진동자 저울 진동수 기울기로 식각률을 재 이온·라디칼 시너지를 직접 얻는다',
      component: QcmSynergySim,
      available: true,
      category: 'process',
      order: 9.7,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-06',
        author: 'Semiconductor Simulator Team',
        features: ['XeF₂ 밸브·Ar⁺ 빔 켜고 끄기', 'Sauerbrey Δf → 식각률', '구간 기울기 기록', '논문값 비교'],
      },
    });

    this.register({
      id: 'ale',
      name: 'ALE — 원자층 식각',
      icon: '🧱',
      description: '개질(라디칼)·제거(이온)를 시간으로 나눈 식각 — 자기 제한, ALE 창, 불소 vs 염소',
      component: AleSim,
      available: true,
      category: 'process',
      order: 9.8,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-06',
        author: 'Semiconductor Simulator Team',
        features: ['주기당 식각량(EPC) vs 이온 에너지', '자기 제한(개질·제거 시간 포화)', '불소 vs 염소', '덮임 θ 시간 흐름'],
      },
    });

    // 서고 전용 — 이온주입 라인(한 화면·단계 탭). 데모 도핑 화면(implantation)은 그대로 두고 곁에 둔다.
    this.register({
      id: 'implant-line',
      name: '이온주입 라인 — 웨이퍼 한 장을 끝까지',
      icon: '🧪',
      description: '빔라인 질량 분석 → 주입(격자 손상·비정질화) → 어닐(재결정·확산·활성화) → SIMS·4탐침 측정 → 소자 미션 채점',
      component: ImplantLineSim,
      available: true,
      category: 'process',
      order: 9.05,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-07',
        author: 'Semiconductor Simulator Team',
        features: ['분석 자석·슬릿으로 이온 고르기(BF₂⁺ 에너지 몫)', '격자 단면 비정질화', 'SPE 재결정·EOR·활성 상한', 'SIMS·4탐침 측정', '소자 미션 채점'],
      },
    });

    // 확산 물리 모델판 — 위 implantation 의 '확산 공정' 탭(개념 화면)은 그대로 두고 곁에 더한다(데모에서 이식, 2026-11-03).
    this.register({
      id: 'diffusion-two-step',
      name: '두 단계로 접합 만들기 (물리 모델)',
      icon: '🔥',
      description: '정원 확산(표면 농도 고정 → 도즈)과 드라이브인(표면을 막고 굽기 → 깊이)을 이어 접합 깊이·표면 농도를 맞춘다',
      component: DiffusionTwoStepLab,
      available: true,
      category: 'process',
      order: 9.06,
      metadata: { version: '1.0.0', lastUpdated: '2026-11-03', author: 'Semiconductor Simulator Team',
        features: ['D = D₀·e^(−Q/kT)', '정원 확산 erfc · 도즈 Q = 2Cs√(Dt/π)', '드라이브인 도즈 보존(상의 방법)', '가우스 근사와 비교', '확산로 3D'] },
    });

    // 데모 '시스템 구조' 두 화면 — 서고의 옛 구조 탭(plasma-i system-structure · plasma-ii icp-system)은 그대로 두고 곁에 둔다.
    this.register({
      id: 'ccp-structure',
      name: 'CCP 시스템 구조 (정밀 개략도)',
      icon: '🏗️',
      description: '창고 심볼로 그린 CCP 장비 단면 + 3D 시스 장면 + 자기 바이어스 오실로 — 손잡이를 돌리면 도면이 같이 움직인다',
      component: CcpStructureSim,
      available: true,
      category: 'process',
      order: 6.4,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-06',
        author: 'Semiconductor Simulator Team',
        features: ['정밀 장비 개략도(부품 짚기)', '3D 시스·면적비 장면', 'Self-bias 오실로스코프', '시스 확대'],
      },
    });

    this.register({
      id: 'icp-structure',
      name: 'ICP 시스템 구조 (정밀 개략도)',
      icon: '🔬',
      description: '유도결합 4단계 3D 장면 + 소스·바이어스 두 손잡이가 움직이는 ICP 장비 단면',
      component: IcpStructureSim,
      available: true,
      category: 'process',
      order: 6.45,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-06',
        author: 'Semiconductor Simulator Team',
        features: ['유도결합 4단계 3D', '소스/바이어스 독립 제어', '윈도우·웨이퍼 시스(Child 법칙)', 'CCP vs ICP 비교표'],
      },
    });

    // 서고 전용 — 본진 /learn/ccp-self-bias 의 다음 단계(A 결과로 잇기 · C 측정해서 얻기).
    this.register({
      id: 'ccp-etch',
      name: 'CCP → 식각 결과',
      icon: '🔩',
      description: 'CCP 자기 바이어스 모델의 시스 전압·밀도로 웨이퍼 이온을 정해 시너지 표면 모델로 깎는다 — 파워가 에너지와 양을 같이 움직인다',
      component: CcpEtchSim,
      available: true,
      category: 'process',
      order: 9.65,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-06',
        author: 'Semiconductor Simulator Team',
        features: ['이온 에너지 V̄₁ × 이온 양(Bohm 플럭스) 평면', '파워 경로 vs ICP 기준점', '바닥/측벽·이방성', '물리 스퍼터 몫'],
      },
    });

    this.register({
      id: 'ccp-q-measure',
      name: '면적비 지수 q 재기',
      icon: '📐',
      description: 'RF 전극을 바꿔 끼우며 V_rf·V_dc 를 읽고 ln(V̄₁/V̄₂) vs ln(A₂/A₁) 기울기로 q 를 구한다',
      component: CcpQMeasureSim,
      available: true,
      category: 'process',
      order: 6.65,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-06',
        author: 'Semiconductor Simulator Team',
        features: ['전극 교체 측정', '원점 지나는 최소제곱 q ± 표준오차', '판독 잡음·평균 횟수', '접지 면적 정의의 함정'],
      },
    });

    // Etching 시뮬레이터 (완성됨)
    this.register({
      id: 'etching',
      name: 'Etching',
      icon: '⚗️',
      description: '건식/습식 식각 공정',
      component: EtchingSimulator,
      available: true,
      category: 'process',
      order: 7,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2025-02-01',
        author: 'Semiconductor Simulator Team',
        features: [
          '식각 공정 개요 (습식/건식 식각)',
          '식각 요소 및 메커니즘 (RIE, 가스 종류)',
          '플라즈마 식각 단계별 프로세스',
          '3D 실리콘 식각 시뮬레이션 (Three.js)',
          '다양한 식각 가스 비교 (SF₆, CF₄, Cl₂, HBr)',
          '선택비 및 이방성 개념',
          '식각 평가 퀴즈 (10문제)'
        ]
      }
    });

    // Deposition 시뮬레이터 (완성됨)
    this.register({
      id: 'deposition',
      name: 'Deposition',
      icon: '📦',
      description: '박막 증착 공정',
      component: DepositionSimulator,
      available: true,
      category: 'process',
      order: 8,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2025-01-24',
        author: 'Semiconductor Simulator Team',
        features: [
          '증착공정 개요 및 이론 학습',
          'PVD 증발 분자 시뮬레이션',
          'PVD 스퍼터링 3D 분자 시각화',
          'Thermal CVD 고온 열분해 메커니즘',
          'PECVD 플라즈마 보조 증착',
          'ALD 원자층 증착 사이클',
          '상세한 이론 설명 및 비교',
          '증착 기술 평가 퀴즈'
        ]
      }
    });

    // Implantation 시뮬레이터 (완성됨)
    this.register({
      id: 'implantation',
      name: 'Implantation',
      icon: '⚛️',
      description: '이온주입 및 도핑',
      component: DopingProcessSimulator,
      available: true,
      category: 'process',
      order: 9,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2025-02-10',
        author: 'Semiconductor Simulator Team',
        features: [
          '도핑 공정 5단계 이론 스토리텔링',
          '이온주입(Implantation) 프로파일 시뮬레이션',
          '확산(Diffusion) 공정 시뮬레이션',
          '어닐링 및 활성화 과정',
          '도즈량(Dose) 및 에너지 최적화',
          'Gaussian 분포 프로파일 분석',
          'LSS 이론 기반 계산',
          '도핑 비교 및 Quiz'
        ]
      }
    });

    // 배선검사패키징 시뮬레이터 (완성됨)
    this.register({
      id: 'metallization-eds-packaging',
      name: '배선검사패키징',
      icon: '🔌',
      description: '금속배선, EDS, 패키징',
      component: MetallizationEDSPackagingSimulator,
      available: true,
      category: 'process',
      order: 10,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2025-12-08',
        author: 'Semiconductor Simulator Team',
        features: [
          '금속배선 (Metallization) 다층 구조 시각화',
          'Damascene 공정 시뮬레이션 (Single/Dual)',
          'EDS (Electrical Die Sorting) 5단계 프로세스',
          '웨이퍼 맵 및 수율 시뮬레이션',
          '패키징 타입 비교 (DIP, QFP, BGA, CSP, FC-BGA)',
          '2.5D/3D 첨단 패키징 (CoWoS, HBM, TSV)',
          'Wire Bonding vs Flip Chip 본딩 비교',
          '금속배선, EDS, 패키징 평가 퀴즈 (10문제)'
        ]
      }
    });

    // 다마신 물리 모델판 — 위 배선검사패키징의 'Damascene (다마신)' 단계 시연(개념 화면)은 그대로 두고 곁에 더한다(데모에서 이식, 2026-11-03).
    this.register({
      id: 'damascene-lab',
      name: '다마신 배선 실습 (물리 모델)',
      icon: '🧵',
      description: '선폭·배리어 두께·싱글/듀얼로 선·비아 저항을, 패턴 밀도·넓은 배선 폭·과연마로 CMP 침식·디싱·잔류 Cu 를 계산한다 — CMP 로 잃은 두께가 선 저항으로 이어진다',
      component: DamasceneLab,
      available: true,
      category: 'process',
      order: 10.05,
      metadata: { version: '1.0.0', lastUpdated: '2026-11-03', author: 'Semiconductor Simulator Team',
        features: ['배리어가 먹는 단면 · 크기효과 ρ(1+(λ/2)(1/w+1/h))', '비아 저항 싱글 vs 듀얼(배리어 1장/2장)', 'CMP 침식 F·ρ/(1−ρ) · 디싱 · 잔류 Cu', '미션 넷(예측 → 확인)'] },
    });

    // 후공정 물리 모델판 — 위 배선검사패키징의 EDS·패키징·본딩 탭(개념 화면)은 그대로 두고 곁에 더한다(데모에서 이식, 2026-11-03).
    this.register({
      id: 'eds-yield-lab',
      name: 'EDS 수율 실습 (물리 모델)',
      icon: '🔍',
      description: '결함 밀도·칩 크기·뭉침 정도로 수율(포아송·음이항)을 계산하고, 웨이퍼 맵 불량 패턴에서 먼저 의심할 곳을 고른다',
      component: EdsYieldLab,
      available: true,
      category: 'process',
      order: 10.1,
      metadata: { version: '1.0.0', lastUpdated: '2026-11-03', author: 'Semiconductor Simulator Team',
        features: ['웨이퍼당 칩 수(격자 배치)', '수율 Y=e^(−AD) · (1+AD/α)^(−α)', '감마-포아송 웨이퍼 맵', '불량 패턴 5종 판독', '프로브 카드 3D'] },
    });
    this.register({
      id: 'hbm-stack-lab',
      name: 'HBM 적층 실습 (물리 모델)',
      icon: '📦',
      description: '세대·층 수·접합 방식·스택 수로 대역폭·용량·다이 한 장 두께를 계산하고 실제 두께 비율 단면으로 본다',
      component: HbmLab,
      available: true,
      category: 'process',
      order: 10.2,
      metadata: { version: '1.0.0', lastUpdated: '2026-11-03', author: 'Semiconductor Simulator Team',
        features: ['HBM2~HBM4 버스 폭·핀 속도', '대역폭 = 버스×핀/8', '높이 한도 안 다이 두께', '범프 vs 하이브리드 본딩', '인터포저 3D'] },
    });
    this.register({
      id: 'bonding-io-lab',
      name: '본딩 연결점 실습 (물리 모델)',
      icon: '🔗',
      description: '와이어·플립칩·마이크로범프·하이브리드 본딩의 연결점 수(둘레 ∝L, 면 ∝L²)와 신호 길이를 칩 크기·피치로 비교한다',
      component: BondingLab,
      available: true,
      category: 'process',
      order: 10.3,
      metadata: { version: '1.0.0', lastUpdated: '2026-11-03', author: 'Semiconductor Simulator Team',
        features: ['방식별 피치 범위', '둘레 vs 면 연결점 수', '교차 칩 크기 L*=4p_a²/p_w', '신호 길이', '조립 3D'] },
    });

    // 종합평가 시뮬레이터 (완성됨)
    this.register({
      id: 'comprehensive-assessment',
      name: '종합평가',
      icon: '🏆',
      description: '전체 공정 통합 평가',
      component: ComprehensiveAssessmentSimulator,
      available: true,
      category: 'assessment',
      order: 11,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2025-12-08',
        author: 'Semiconductor Simulator Team',
        features: [
          '기초 레벨 15문제 (진공, 세정, 산화, 리소)',
          '심화 레벨 15문제 (플라즈마, 식각, 증착, 도핑, 배선)',
          '전체 시뮬레이터 통합 문제',
          '점수 및 등급 평가',
          '오답 해설 제공'
        ]
      }
    });

    // ALD 시뮬레이터 (완성됨)
    this.register({
      id: 'ald',
      name: 'ALD 원자층증착',
      icon: '🔬',
      description: 'Atomic Layer Deposition 시뮬레이션',
      component: ALDSimulator,
      available: true,
      category: 'process',
      order: 12,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-03-06',
        author: 'Semiconductor Simulator Team',
        features: [
          'ALD 사이클 시뮬레이션',
          'Self-limiting 반응 원리',
          'ALD Window 확인',
          '다양한 물질 (Al₂O₃, HfO₂, TiN)',
          'GPC(Growth Per Cycle) 분석'
        ]
      }
    });

    // PECVD 시뮬레이터 (완성됨)
    this.register({
      id: 'pecvd',
      name: 'PECVD',
      icon: '⚡',
      description: 'Plasma Enhanced CVD 시뮬레이션',
      component: PECVDSimulator,
      available: true,
      category: 'process',
      order: 13,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-03-06',
        author: 'Semiconductor Simulator Team',
        features: [
          'SiN/SiO₂ 증착 시뮬레이션',
          'RF 파워와 막질 관계',
          '스트레스 제어',
          '가스 비율 최적화'
        ]
      }
    });

    // LPCVD 시뮬레이터 (완성됨)
    this.register({
      id: 'lpcvd',
      name: 'LPCVD',
      icon: '🔥',
      description: 'Low Pressure CVD 시뮬레이션',
      component: LPCVDSimulator,
      available: true,
      category: 'process',
      order: 14,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-03-06',
        author: 'Semiconductor Simulator Team',
        features: [
          'Poly-Si 증착 시뮬레이션',
          'Si₃N₄ 저압 증착',
          '표면반응 제한 모드',
          '균일도 분석'
        ]
      }
    });

    // RTA 시뮬레이터 (완성됨)
    this.register({
      id: 'rta',
      name: 'RTA 급속열처리',
      icon: '🌡️',
      description: 'Rapid Thermal Annealing 시뮬레이션',
      component: RTASimulator,
      available: true,
      category: 'process',
      order: 15,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-03-06',
        author: 'Semiconductor Simulator Team',
        features: [
          '온도 프로파일 시뮬레이션',
          'Spike anneal vs Soak anneal',
          '도펀트 활성화율 분석',
          '열 버짓 계산'
        ]
      }
    });

    // Sputtering 시뮬레이터 (완성됨)
    this.register({
      id: 'sputtering',
      name: '스퍼터링',
      icon: '💫',
      description: 'DC/RF Magnetron Sputtering 시뮬레이션',
      component: SputteringSimulator,
      available: true,
      category: 'process',
      order: 16,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-03-06',
        author: 'Semiconductor Simulator Team',
        features: [
          'DC/RF Magnetron 모드',
          '타겟 물질별 증착',
          'Reactive Sputtering',
          '증착 속도/균일도 분석'
        ]
      }
    });

    // Evaporator 시뮬레이터 (완성됨)
    this.register({
      id: 'evaporator',
      name: '열증발',
      icon: '🔭',
      description: 'Thermal/E-beam Evaporation 시뮬레이션',
      component: EvaporatorSimulator,
      available: true,
      category: 'process',
      order: 17,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-03-06',
        author: 'Semiconductor Simulator Team',
        features: [
          'Resistive vs E-beam 가열',
          '다양한 소스 물질',
          '증착 속도 시뮬레이션',
          'Tooling factor 계산'
        ]
      }
    });

    // SEM — 데모에서 들인 'SEM 오퍼레이터 실습'(물리 모델판). 예전 SEM 콘솔(analysis-sem.html)을 대체했다
    // (담당자 2026-11-03 «기존에 있는 건 없애고 — 우리 그거 바꾼 거잖아»). id 'sem' 은 저장된 수업 계획이 가리키므로 그대로 둔다.
    this.register({
      id: 'sem',
      name: 'SEM 오퍼레이터 실습',
      icon: '🔬',
      description: '튜토리얼(장비 켜기 → 초점·난시 → 촬영 → 측정) + 미션 5개(콘택홀·증착막·트렌치·다층막·대전)',
      component: SEMSimulator,
      available: true,
      category: 'analysis',
      order: 19,
      metadata: {
        version: '2.0.0',
        lastUpdated: '2026-11-03',
        author: 'Semiconductor Simulator Team',
        features: [
          '프로브 지름(회절·수차)·초점 심도·샷 잡음을 식으로 계산한 가상 SEM 상',
          '틸트 3D 관찰과 쪼갠 면 단면 — 기울기 보정(sin θ)으로 깊이 재기',
          '워블로 난시 진단, 히스토그램으로 밝기·대비',
          '감광막 대전 — E2 근처 저전압·전류·코팅으로 대처',
          '증착막 결정립 절편법 · 트렌치 깊이 · 다층막 층별 두께(단면 홀더 90°)',
        ],
      },
    });

    // RGA 시뮬레이터 (완성됨) - 시료 분석 센터
    this.register({
      id: 'rga',
      name: 'RGA 잔류가스분석기',
      icon: '📈',
      description: 'Residual Gas Analyzer 질량 스펙트럼 분석',
      component: RGASimulator,
      available: true,
      category: 'analysis',
      order: 20,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-04-13',
        author: 'Semiconductor Simulator Team',
        features: [
          '질량 스펙트럼 실시간 분석',
          '리크 / 아웃가싱 진단',
          '잔류가스 종류 식별',
          '진공 상태 품질 평가'
        ]
      }
    });

    // OES 시뮬레이터 (완성됨) - 시료 분석 센터
    this.register({
      id: 'oes',
      name: 'OES 광학방출분광기',
      icon: '🌈',
      description: 'Optical Emission Spectroscopy 플라즈마 분석',
      component: OESSimulator,
      available: true,
      category: 'analysis',
      order: 21,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-04-13',
        author: 'Semiconductor Simulator Team',
        features: [
          '플라즈마 발광 스펙트럼 분석',
          '반응 종 식별',
          '식각 엔드포인트 검출',
          '공정 실시간 모니터링'
        ]
      }
    });

    // XPS 시뮬레이터 (완성됨) - 시료 분석 센터
    this.register({
      id: 'xps',
      name: 'XPS 광전자분광기',
      icon: '⚛️',
      description: 'X-ray Photoelectron Spectroscopy 표면 화학분석',
      component: XPSSimulator,
      available: true,
      category: 'analysis',
      order: 22,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-04-29',
        author: 'Semiconductor Simulator Team',
        features: [
          'X선 광전자 스펙트럼 분석',
          '표면 원소 조성 정량 분석',
          '화학결합 상태 (binding energy) 식별',
          'Survey / High-resolution 스캔 모드'
        ]
      }
    });

    // 나노소자 비교 시뮬레이터 (완성됨)
    this.register({
      id: 'nano-device',
      name: '나노소자 비교',
      icon: '🔬',
      description: 'Si MOSFET vs GaN HEMT 나노소자 구조 비교 시뮬레이션',
      component: NanoDeviceComparisonSimulator,
      available: true,
      category: 'analysis',
      order: 18,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-03-06',
        author: 'Semiconductor Simulator Team',
        features: [
          'Si MOSFET / GaN HEMT 비교',
          '2D 단면도 SVG 시각화',
          '3D 소자 구조 뷰어',
          '8단계 제조 공정 비교',
          'NPC 가이드 대화'
        ]
      }
    });

    // 메모리·CPU 산업 이론 패널 (Day 13)
    this.register({
      id: 'memory-industry',
      name: '메모리·CPU 산업',
      icon: '🏭',
      description: 'DRAM/NAND/로직/파운드리 — 기술과 산업 지형',
      component: MemoryIndustrySimulator,
      available: true,
      category: 'analysis',
      order: 19,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-06-15',
        author: 'Semiconductor Simulator Team',
        features: ['메모리 vs 로직', 'DRAM 1T1C', '3D NAND', '로직/파운드리', '산업 트렌드']
      }
    });

    // 반도체 소자 아키텍처 맵 (소자·산업 전체 오버뷰)
    this.register({
      id: 'device-architecture-map',
      name: '소자 아키텍처 맵',
      icon: '🗺️',
      description: '트랜지스터·메모리·전력·패키징 4축을 한눈에 탐색하는 소자 아키텍처 지도',
      component: DeviceArchitectureMapSimulator,
      available: true,
      category: 'analysis',
      order: 20,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-07-23',
        author: 'Semiconductor Simulator Team',
        features: [
          '트랜지스터 진화(Planar→FinFET→GAA→CFET→VTFET)',
          '메모리 구조(DRAM·V-NAND·HBM·차세대)',
          '전력·화합물(IGBT·SiC·GaN·GaAs)',
          '패키징 진화(2D·2.5D·3D·칩렛)',
          '노드별 SVG 단면 구조도 + 설명'
        ]
      }
    });

    // 나노·신소재 — MOF 책(topic.nano.mof)의 두 화면. 서고 전용(appendix 문턱 11 아래에 둔다).
    this.register({
      id: 'nano-nobel-tour',
      name: '나노신소재 노벨상 탐방',
      icon: '🏅',
      description: '양자점·풀러렌·MOF·청색 LED·그래핀 — 처음 발견에서 노벨상까지, MOF 우리에 하나씩 담는 만화 탐방',
      component: NobelNanoTour,
      available: true,
      category: 'analysis',
      order: 9.91,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-13',
        author: 'Semiconductor Simulator Team',
        features: ['발견 → 수상 연표(걸린 해)', '회전하는 MOF 골격 무대', '장면별 그때의 질문·원리'],
      },
    });
    this.register({
      id: 'nano-mof-viewer',
      name: 'MOF 레티큘러 뷰어',
      icon: '🧊',
      description: '금속 마디는 그대로, 유기 막대만 바꿔 같은 그물이 커지는 것을 3D 로 본다(IRMOF-1·10·16)',
      component: MofReticularViewer,
      available: true,
      category: 'analysis',
      order: 9.92,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-13',
        author: 'Semiconductor Simulator Team',
        features: ['막대 고리 1·2·3개 전환', '빈 방(구멍) 시각화', '표면적 감 잡기'],
      },
    });

    // 나노·신소재 — 나노의 세계 책(topic.nano.world). 엔진 src/physics/nanoScale.js(서고 전용).
    this.register({
      id: 'nano-surface-atoms',
      name: '표면 원자 — 작아지면 겉이 전부다',
      icon: '🟡',
      description: '금 나노 정육면체를 원자 하나하나 쌓아 겉 원자(이웃이 모자란 원자)의 비율을 3D 와 곡선으로 본다',
      component: SurfaceAtomsSim,
      available: true,
      category: 'analysis',
      order: 9.95,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['원자를 센 겉 비율', '0.4~100 nm 곡선', '이웃 수로 칠한 3D'] },
    });
    this.register({
      id: 'nano-nucleation',
      name: '핵생성 — 넘어야 할 언덕',
      icon: '⛰️',
      description: '과포화를 바꿔 ΔG(r) 언덕·임계 반지름 r*·핵 생성 빈도가 어떻게 바뀌는지 본다(고전 핵생성 이론)',
      component: NucleationSim,
      available: true,
      category: 'analysis',
      order: 9.96,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['ΔG(r) 곡선', 'r*·ΔG*', '핵 생성 빈도 배율', '비커(방향)'] },
    });
    // 나노의 세계 보강 — 표면에너지 · 분산 · DLVO. 엔진 src/physics/colloid.js(서고 전용, Huynh & Chen 2011 실측 대조).
    this.register({
      id: 'nano-oled',
      name: 'OLED — 넣은 전자 넷 중 하나만 빛나는 이유',
      icon: '📱',
      description: '형광 · 인광 · TADF 와 굴절률을 바꿔 스핀 통계(1:3)와 광추출이 외부 양자 효율을 어떻게 정하는지 본다(실측 J. Appl. Phys. 2001 · Nature 2012)',
      component: OledSim,
      available: true,
      category: 'analysis',
      order: 9.9606,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-18', author: 'Semiconductor Simulator Team',
        features: ['스핀 통계', '형광 · 인광 · TADF', '광추출 1/(2n²)', '실측 대조'] },
    });
    // LED 책 보강 — LCD(신소재특강 4강). 엔진 src/physics/lcd.js(서고 전용) — 문턱 π√(K/ε₀Δε) · 꼬인 셀 투과(J. Phys. D 1975) · 존스 계산.
    this.register({
      id: 'nano-lcd',
      name: 'LCD — 빛을 만드는 대신 거르는 화면',
      icon: '🖥️',
      description: '화소 전압 · 셀 두께 · 개구율을 바꿔 90° 꼬인 액정(TN)이 편광을 돌리는지, 문턱 전압(π√(K/ε₀Δε))과 V-T 곡선, 흰 화면도 백라이트의 몇 % 만 나오는 까닭을 3D 화소 한 칸으로 본다',
      component: LcdPixelSim,
      available: true,
      category: 'analysis',
      order: 9.9607,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-19', author: 'Semiconductor Simulator Team',
        features: ['TN 액정 3D', '문턱 전압', 'V-T 곡선', '편광 안내', '빛 장부'] },
    });
    // LED 책 보강 — OLED(센서재료 10편). 엔진 src/physics/oled.js(서고 전용).
    this.register({
      id: 'nano-dlvo',
      name: '소금 한 꼬집에 엉긴다 — DLVO',
      icon: '🧂',
      description: '소금 농도 · 이온 가수 · 코팅을 바꿔 나노입자 사이 에너지 장벽(10 kT 기준)과 실제로 엉기는 모습을 본다(DLVO · 푹스 안정도 비)',
      component: DlvoSim,
      available: true,
      category: 'analysis',
      order: 9.9605,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-18', author: 'Semiconductor Simulator Team',
        features: ['DLVO 곡선', '임계 응집 농도', '2가 이온', '입체 안정화', '실측 대조(시트르산 은)'] },
    });
    // 나노·신소재 — 탄소 책(topic.nano.carbon). 엔진 src/physics/carbonNano.js(서고 전용).
    this.register({
      id: 'nano-carbon-gallery',
      name: '탄소 동소체 3D 갤러리',
      icon: '💎',
      description: '다이아몬드·흑연·풀러렌·탄소나노튜브·그래핀 — 같은 탄소가 결합과 차원에 따라 다른 모양이 되는 것을 원자 공·막대 3D 로',
      component: CarbonAllotropeGallery,
      available: true,
      category: 'analysis',
      order: 9.93,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['좌표는 결정학 식에서', 'sp³ vs sp²', '0D·1D·2D·3D'] },
    });
    this.register({
      id: 'nano-cnt-rollup',
      name: '그래핀을 말아 CNT 만들기',
      icon: '🧻',
      description: '감는 벡터 (n,m) 을 골라 그래핀 판을 말고, 지름·키랄각·금속/반도체를 판정한다',
      component: CntRollupSim,
      available: true,
      category: 'analysis',
      order: 9.94,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['(n,m) 고르기', '판이 말리는 3D 애니메이션', '(n−m) mod 3 판정', '지름·키랄각'] },
    });
    // 나노·신소재 — 2D 소재 책(topic.nano.twoD). 엔진 src/physics/twoD.js(서고 전용).
    this.register({
      id: 'nano-vdw-stack',
      name: '반데르발스 레고 — 2D 소재 쌓기',
      icon: '🧱',
      description: '그래핀·hBN·MoS₂ 를 한 장씩 얹어 3D 로 쌓고, 두께와 층 수에 따른 MoS₂ 띠틈(Mak 2010 측정값)을 본다',
      component: VdwStackSim,
      available: true,
      category: 'analysis',
      order: 9.97,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['층 안은 결합 · 층 사이는 틈', '새 장이 내려앉는 3D', 'MoS₂ 1장 직접 → 2장 간접', '이종 적층'] },
    });
    this.register({
      id: 'nano-moire',
      name: '비틀면 무늬가 생긴다 — 모아레',
      icon: '🌀',
      description: '그래핀 위에 그래핀·hBN 을 비틀어 얹어 원자 배열과 겹침 무늬를 보고, 무늬 주기 λ 와 마법각 1.1° 를 읽는다',
      component: MoireSim,
      available: true,
      category: 'analysis',
      order: 9.98,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['원자 가까이 · 무늬 멀리', 'λ = a/(2 sin θ/2)', '그래핀/hBN 격자 차 1.8%', '마법각'] },
    });
    // 나노·신소재 — LED 책(topic.nano.led). 엔진 src/physics/led.js(서고 전용).
    this.register({
      id: 'nano-led-color',
      name: '조성으로 색 고르기 — 질화물 LED',
      icon: '💡',
      description: 'InGaN·AlGaN 의 In·Al 비율을 바꿔 띠틈과 빛의 파장·색을 본다. 굽힘 값의 문헌 차이를 띠로 그려 "몇 %" 를 범위로 읽는다',
      component: LedColorSim,
      available: true,
      category: 'analysis',
      order: 9.985,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['파장 = 1240 / 띠틈', '굽힘 범위 띠', '파랑·초록·UV-C 바로가기', '강의 소재 표'] },
    });
    this.register({
      id: 'nano-mocvd-growth',
      name: 'MOCVD — 원료가 내려와 결정이 된다',
      icon: '🔥',
      description: 'TMGa·TMIn·NH₃ 가 뜨거운 기판 위에서 쪼개져 우르츠광 GaN 격자에 한 겹씩 앉는 3D 장면. 온도에 따라 In 이 남거나 떠난다',
      component: MocvdGrowthSim,
      available: true,
      category: 'analysis',
      order: 9.995,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['원료 주입 → 열분해 → 성장 → 부산물', '우르츠광 GaN 격자', '고온/저온 In 탈착', '겹별 In 비율 → 빛'] },
    });
    // 나노·신소재 — 양자점·페로브스카이트 책(topic.nano.qd). 엔진 src/physics/qdot.js(서고 전용).
    this.register({
      id: 'nano-qd-size',
      name: '크기로 색 고르기 — CdSe 양자점',
      icon: '🔴',
      description: 'CdSe 양자점을 원자로 쌓아 지름을 바꾸며 빛 색을 본다. 단순 모델(Brus 식)과 실측 곡선(Yu 2003)이 작은 크기에서 갈라지는 것까지',
      component: QdSizeSim,
      available: true,
      category: 'analysis',
      order: 9.9965,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['원자로 쌓은 3D 점', '실측 vs 단순 모델', '겉 원자 몫', '2~8 nm'] },
    });
    this.register({
      id: 'nano-perovskite',
      name: '페로브스카이트 ABX₃ — 팔면체 골격과 빈 칸',
      icon: '🧊',
      description: 'PbX₆ 팔면체가 꼭짓점을 나눠 이은 골격에 A 양이온(Cs·MA·FA)을 넣고, 할로젠을 바꾸며 허용 인자와 띠틈을 본다',
      component: PerovskiteSim,
      available: true,
      category: 'analysis',
      order: 9.9975,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['꼭짓점 공유 팔면체 3D', '골드슈미트 허용 인자', 'I·Br·Cl 띠틈', 'Cs·MA·FA 크기'] },
    });
    // 나노·신소재 — 메타소재·차폐·저장 책(topic.nano.meta). 엔진 src/physics/meta.js(서고 전용).
    this.register({
      id: 'nano-meta-refraction',
      name: '음의 굴절 — 빛이 반대쪽으로 꺾인다',
      icon: '🪞',
      description: '굴절률을 −2~+2 로 바꾸며 스넬 법칙으로 꺾이는 방향과 물결면의 흐름을 보고, n = −1 평판이 빛을 다시 모으는 평판 렌즈를 본다',
      component: MetaRefractionSim,
      available: true,
      category: 'analysis',
      order: 9.9985,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['스넬 법칙 · 음수 각', '위상 vs 에너지', '베셀라고 평판 렌즈', '전반사'] },
    });
    this.register({
      id: 'nano-emi-shield',
      name: '전자파 차폐 — 얼마나 막나',
      icon: '🛡️',
      description: '구리·알루미늄·스테인리스·MXene 필름의 두께와 주파수를 바꾸며 반사·흡수·다중반사로 차폐(dB)를 계산하고, 논문 측정값과 비교한다',
      component: EmiShieldSim,
      available: true,
      category: 'analysis',
      order: 9.9992,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-14', author: 'Semiconductor Simulator Team',
        features: ['표피 깊이', 'R + A + B', 'dB → 막는 몫', 'MXene 측정값 비교'] },
    });
    // 메타소재 책 보강 — 초전도. 엔진 src/physics/superconductor.js(서고 전용) — 실측 Tc · Bc(T) = Bc0(1−(T/Tc)²) · 완전 반자성 구 정확해.
    this.register({
      id: 'nano-supercon',
      name: '초전도 — 자석을 띄워라',
      icon: '🧲',
      description: '초전도 원판(YBCO · NbTi · Nb₃Sn · MgB₂ · Nb · Pb · Hg)을 액체 질소 · 수소 · 헬륨으로 식혀 네 탐침 저항이 0 이 되는 순간 자석이 밀려 뜨는 것(마이스너 효과)을 보고, 자석 장을 키워 임계 자기장을 넘긴다',
      component: SuperconductorSim,
      available: true,
      category: 'analysis',
      order: 9.99925,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-19', author: 'Semiconductor Simulator Team',
        features: ['저항 0 · 네 탐침', '마이스너 효과 3D', '임계 온도 · 냉매', '임계 자기장 Bc(T)', '제2종 자속 고정'] },
    });
    // 화합물 반도체 책(topic.nano.compound) — 엔진 src/physics/compoundSemi.js(서고 전용). 재료 표는 총설 한 장(BFOM 재현 테스트).
    this.register({
      id: 'nano-power-chip',
      name: '전력 반도체 — 같은 전압, 몇 배 작은 칩',
      icon: '⚡',
      description: '버틸 전압과 목표 저항을 정해 실리콘 · SiC · GaN · 산화 갈륨 칩을 150 mm 웨이퍼 위에 실제 비율로 놓고, 표류층 두께 · 전계 삼각형 · 칩이 오르는 온도를 견준다(R_on,sp = 4BV²/(εμEc³))',
      component: PowerChipSim,
      available: true,
      category: 'analysis',
      order: 9.99926,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-24', author: 'Semiconductor Simulator Team',
        features: ['칩 면적 실제 비율 3D', '표류층 전계 삼각형', 'BV² 법칙', '열 — 퍼짐 저항 어림'] },
    });
    this.register({
      id: 'nano-gan-hemt',
      name: 'GaN HEMT — 도핑 없이 생기는 전자 바다',
      icon: '📡',
      description: 'AlGaN 의 Al 비율 · 장벽 두께 · 게이트 전압을 바꿔 분극 전하가 만드는 2차원 전자 가스(2DEG)와 띠 그림, 임계 두께 · 문턱 전압을 본다(J. Appl. Phys. 2000 모형)',
      component: HemtSim,
      available: true,
      category: 'analysis',
      order: 9.99927,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-24', author: 'Semiconductor Simulator Team',
        features: ['HEMT 단면 3D', '분극 · 경계 전하', '2DEG 밀도 · 면 저항', '전도띠 그림', '게이트 문턱'] },
    });
    // 양자소자 책(topic.nano.quantum) — 엔진 src/physics/quantumDevices.js(서고 전용). 전달 행렬은 사각 장벽 닫힌 식 · 유한 우물 준위와 테스트로 묶임.
    this.register({
      id: 'nano-tunneling',
      name: '터널링 — 벽을 뚫는 전자',
      icon: '🌊',
      description: '벽 두께 · 높이 · 전자 에너지를 바꿔 파동함수가 벽 속에서 줄어드는 모습(3D 나선)과 통과 확률을 보고, 벽 둘에서 공명으로 100 % 통과 · 공명 터널 다이오드의 음의 미분 저항을 찾는다',
      component: TunnelSim,
      available: true,
      category: 'analysis',
      order: 9.99928,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-24', author: 'Semiconductor Simulator Team',
        features: ['파동함수 나선 3D', '전달 행렬 투과', '공명 터널링', 'RTD 전류-전압'] },
    });
    this.register({
      id: 'nano-qubit',
      name: '큐비트 — 블로흐 구를 돌려라',
      icon: '🔮',
      description: 'π · π/2 펄스로 블로흐 구 위 큐비트 상태를 돌리고, 100 번 재 확률을 세고, 기다리며 결맞음(T₁ · T₂)을 잃는 것과 주파수가 어긋날 때 못 뒤집는 것을 본다(블로흐 방정식)',
      component: QubitSim,
      available: true,
      category: 'analysis',
      order: 9.99929,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-24', author: 'Semiconductor Simulator Team',
        features: ['블로흐 구 3D', '라비 회전', '측정 통계', 'T₁ · T₂ 결맞음', '열 들뜸'] },
    });
    // 유전체 책(topic.nano.dielectric) — 엔진 src/physics/dielectrics.js(서고 전용). 산화막 표 · EOT · 배선 RC 를 테스트로 묶음.
    this.register({
      id: 'nano-high-k',
      name: 'High-k 게이트 — 두꺼운데 얇은 막',
      icon: '🧱',
      description: '같은 EOT(정전 용량)로 SiO₂ · HfO₂ 등 막을 바꿔 막은 두꺼워지는데 모이는 전하는 그대로인 것(3D)과 터널링 누설이 자릿수로 주는 것을 보고, 계면층이 병목인 것과 k 가 큰 TiO₂ 를 안 쓰는 까닭(벽 0 eV)을 찾는다',
      component: HighKSim,
      available: true,
      category: 'analysis',
      order: 9.999292,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-24', author: 'Semiconductor Simulator Team',
        features: ['게이트 스택 3D', 'EOT · 정전 용량', '터널링 누설 배수', '산화막 k · 띠 지도'] },
    });
    this.register({
      id: 'nano-low-k',
      name: 'Low-k 배선 — 선 사이의 숨은 커패시터',
      icon: '🧵',
      description: '배선 피치 · 길이 · 절연막(SiO₂ · FSG · SiCOH · 다공 · 에어갭)을 바꿔 선이 촘촘해질수록 지연이 피치의 제곱으로 느는 것(3D)을 보고, 2 GHz 한 클록 안에 넣으려면 k 를 얼마나 내려야 하는지 · 기공의 대가를 찾는다',
      component: LowKSim,
      available: true,
      category: 'analysis',
      order: 9.999294,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-24', author: 'Semiconductor Simulator Team',
        features: ['배선 단면 3D', 'RC 지연', '다공막 유효 k', '에어갭'] },
    });
    // 센서 책장 — ① 센서 기초(topic.sensor.basics). 엔진 src/physics/sensorBasics.js(서고 전용).
    this.register({
      id: 'sensor-calib',
      name: '교정 곡선으로 성적표 매기기',
      icon: '📏',
      description: '가상 센서 셋(곧은·휘는·시끄러운)의 입력을 바꿔 가며 재고, 최소제곱 직선으로 감도·비직선성(%FS)·잡음·검출 한계를 계산한다',
      component: SensorCalibSim,
      available: true,
      category: 'analysis',
      order: 9.9993,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-15', author: 'Semiconductor Simulator Team',
        features: ['교정 곡선', '감도·오프셋', '비직선성 %FS', '잡음 σ · 3σ/S'] },
    });
    this.register({
      id: 'sensor-response',
      name: '응답 속도와 분해능',
      icon: '⏱️',
      description: '시정수 τ 로 계단 입력을 따라오는 빠르기(t63·t90)를, 변환기 비트 수로 디지털로 읽는 눈금(LSB)을 본다',
      component: SensorResponseSim,
      available: true,
      category: 'analysis',
      order: 9.9994,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-15', author: 'Semiconductor Simulator Team',
        features: ['1차 응답', 't63 · t90', 'n 비트 → 2ⁿ 칸', 'LSB %FS'] },
    });
    // ② 온도센서(topic.sensor.temp). 엔진 src/physics/tempSensors.js(서고 전용).
    this.register({
      id: 'temp-sensor-compare',
      name: '같은 온도, 네 가지 온도계',
      icon: '🌡️',
      description: '실제 온도 하나를 NTC · Pt100 · K형 열전대 · IC 가 동시에 재고, 내놓는 값과 사용 범위 · 비직선성 · 열전대 기준접점 보상을 비교한다',
      component: TempSensorCompareSim,
      available: true,
      category: 'analysis',
      order: 9.9995,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-15', author: 'Semiconductor Simulator Team',
        features: ['NTC B 식', 'Pt100 IEC 60751', 'K형 NIST ITS-90', '기준접점 보상'] },
    });
    this.register({
      id: 'ntc-self-heat',
      name: 'NTC 자기발열과 액면 센서',
      icon: '💧',
      description: '전류를 키우면 서미스터가 스스로 데워져 전압이 꺾인다 — 액체 속과 공기 중의 전류–전압 곡선으로 액면 센서의 원리를 본다',
      component: NtcSelfHeatSim,
      available: true,
      category: 'analysis',
      order: 9.9996,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-15', author: 'Semiconductor Simulator Team',
        features: ['I²R = δ·ΔT', '전압 꺾임 ΔT = T²/B', '액체·공기', '액면 센서'] },
    });
    // ③ 광센서(topic.sensor.light). 엔진 src/physics/photoSensors.js(서고 전용, hc 와 파장 색은 led.js).
    this.register({
      id: 'photo-cutoff',
      name: '띠틈이 정하는 차단 파장',
      icon: '🌈',
      description: '파장을 움직여 광자 에너지가 재료마다의 띠틈(불순물 준위)을 넘는지 보고, 차단 파장 λc = 1.24 / Eg 로 어떤 재료가 어떤 빛을 느끼는지 비교한다',
      component: PhotoCutoffSim,
      available: true,
      category: 'analysis',
      order: 9.99961,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-15', author: 'Semiconductor Simulator Team',
        features: ['E = 1239.84 / λ', 'λc = 1.24 / Eg', '진성 · 불순물 광도전체', '자외선~원적외선'] },
    });
    this.register({
      id: 'photodiode-resp',
      name: '포토다이오드 — 응답도와 빛의 세기',
      icon: '🔆',
      description: '실리콘 흡수 계수(Green 2008)로 파장별 응답도를 계산하고 pn · PIN · 반사 방지막을 비교한다. 빛의 세기에 단락 전류는 정비례, 개방 전압은 로그로 따라온다',
      component: PhotodiodeSim,
      available: true,
      category: 'analysis',
      order: 9.99962,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-15', author: 'Semiconductor Simulator Team',
        features: ['R = ηλ / 1239.84', '흡수 깊이 1/α', 'pn · PIN', 'I_sc ∝ P · V_oc ∝ ln P'] },
    });
    // ④ 초음파·자기센서(topic.sensor.ultramag). 엔진 src/physics/ultrasonicMagnetic.js(서고 전용).
    this.register({
      id: 'ultrasonic-range',
      name: '초음파로 거리 재기',
      icon: '🦇',
      description: '주파수 · 거리 · 기온 · 방사면 지름을 바꿔 메아리가 읽히는지(ISO 9613-1 공기 흡수), 빔이 얼마나 넓은지, 기온을 모르면 얼마나 틀리는지 본다',
      component: UltrasonicRangeSim,
      available: true,
      category: 'analysis',
      order: 9.99963,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-15', author: 'Semiconductor Simulator Team',
        features: ['d = c·t/2', 'c(T)', '원판 지향성 1.22λ/D', 'ISO 9613-1 흡수'] },
    });
    this.register({
      id: 'hall-sensor',
      name: '홀 소자와 홀 IC',
      icon: '🧲',
      description: '자석 거리를 움직여 B 와 홀 전압을 보고, 재료 넷을 정전류 · 정전압으로 비교한다. 홀 IC 는 히스테리시스로 다가올 때와 멀어질 때 바뀌는 거리가 다르다',
      component: HallSensorSim,
      available: true,
      category: 'analysis',
      order: 9.99964,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-15', author: 'Semiconductor Simulator Team',
        features: ['V_H = IB/(nqt)', '정전압 감도 ∝ μ', '원기둥 자석 B(z)', '홀 IC 히스테리시스'] },
    });
    // ⑤ 유량·변위센서(topic.sensor.flow). 엔진 src/physics/flowDisplacement.js(서고 전용).
    this.register({
      id: 'flow-meters',
      name: '같은 흐름, 네 가지 유량계',
      icon: '🌊',
      description: '같은 물의 흐름을 차압 · 와류 · 초음파 · 전자기 유량계가 동시에 잰다 — 차압은 제곱, 나머지는 정비례. 수온을 바꿔도 초음파 시간차 유속은 그대로',
      component: FlowMetersSim,
      available: true,
      category: 'analysis',
      order: 9.99965,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-15', author: 'Semiconductor Simulator Team',
        features: ['ΔP ∝ v²', 'f = St·v/d', '전파 시간차(음속 약분)', 'E = B·D·v'] },
    });
    this.register({
      id: 'thermal-mfc',
      name: '열식 MFC 와 가스 보정 계수',
      icon: '🌡️',
      description: '열식 질량 유량계가 ΔT ∝ ṁ·cp 로 재는 원리와, N₂ 로 교정한 MFC 에 다른 가스를 흘릴 때의 실제 유량(가스 보정 계수)을 반도체 공정 가스로 본다',
      component: ThermalMfcSim,
      available: true,
      category: 'analysis',
      order: 9.99966,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-15', author: 'Semiconductor Simulator Team',
        features: ['ΔT ∝ ṁ·cp', 'GCF = 0.3106·N/(d·cp)', 'sccm → mol/s', '공정 가스 9종'] },
    });
    // ⑥ 칩 위의 기계(topic.sensor.mems). 엔진 src/physics/memsSensors.js(서고 전용).
    this.register({
      id: 'mems-accel',
      name: '가속도계 — 질량 · 스프링 · 빗살 전극',
      icon: '📳',
      description: '가속도 · 공진 주파수 · 증명 질량을 바꿔 질량의 변위(nm) · 빗살 전극 정전용량 · 열잡음을 보고, 감도와 대역 · 크기와 잡음의 맞바꿈을 본다',
      component: MemsAccelSim,
      available: true,
      category: 'analysis',
      order: 9.99967,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-16', author: 'Semiconductor Simulator Team',
        features: ['x = a/ω₀²', 'ΔC ≈ 2C₀x/g', '열잡음 √(4kTω₀/mQ)', '감도 ∝ 1/f₀²'] },
    });
    this.register({
      id: 'mems-pressure',
      name: '압력 센서 — 얇은 막과 휘는 저항',
      icon: '🫧',
      description: 'KOH 로 깎아 남긴 실리콘 막의 처짐 · 응력(Timoshenko)과 압저항 브리지 출력을 압력 · 막 크기 · 두께로 본다. 작은 처짐 식이 맞는 범위도 알려 준다',
      component: MemsPressureSim,
      available: true,
      category: 'analysis',
      order: 9.99968,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-16', author: 'Semiconductor Simulator Team',
        features: ['σ = 0.3078·P·a²/h²', 'π₄₄ 압저항', '휘트스톤 브리지', 'KOH 54.74°'] },
    });
    // ⑦ 가스 센서(topic.sensor.gas). 엔진 src/physics/gasSensors.js(서고 전용).
    this.register({
      id: 'gas-mos',
      name: '반도체식 가스 센서 — 저항이 얼마나 내려가나',
      icon: '🌫️',
      description: '상용 SnO₂ 센서(Figaro TGS2600) 데이터시트 곡선으로 가스 · 농도 · 소자 저항 · 부하저항에 따른 출력 전압을 보고, 다른 가스를 같은 가스로 읽는 선택성 문제와 소자마다 다른 저항(교정)을 본다',
      component: GasMosSim,
      available: true,
      category: 'analysis',
      order: 9.99969,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-16', author: 'Semiconductor Simulator Team',
        features: ['Rs/Ro 데이터시트', 'Vout = Vc·RL/(Rs+RL)', 'Ps ≤ 15 mW', '선택성 · 교정'] },
    });
    this.register({
      id: 'zirconia-o2',
      name: '지르코니아 산소 센서 — 로그로 재는 센서',
      icon: '🧪',
      description: '산소 이온 전도체(YSZ) 양쪽 산소 분압 차이가 만드는 네른스트 전압을 온도 · 산소 분압으로 보고, 공기 근처(산소결핍 18 %)는 갈바니 센서로 재는 이유를 비교한다',
      component: ZirconiaO2Sim,
      available: true,
      category: 'analysis',
      order: 9.9997,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-16', author: 'Semiconductor Simulator Team',
        features: ['E = (RT/4F)·ln(p_ref/p)', '10배당 약 48 mV(700 °C)', '갈바니 I ∝ p', '산소결핍 18 %'] },
    });
    // ⑧ 습도 센서(topic.sensor.humidity). 엔진 src/physics/humiditySensors.js(서고 전용).
    this.register({
      id: 'humidity-air',
      name: '같은 공기, 온도만 바뀌면 — 습도의 말들',
      icon: '💧',
      description: '공기 온도와 이슬점(서리점)으로 상대습도 · 절대습도 · 혼합비 · ppmv 를 보고, 차가운 면에 이슬 · 서리가 맺히는 조건을 포화 수증기압 곡선 위에서 본다',
      component: HumidityAirSim,
      available: true,
      category: 'analysis',
      order: 9.99971,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-16', author: 'Semiconductor Simulator Team',
        features: ['마그누스식(WMO)', '이슬점 · 서리점', 'g/m³ · g/kg · ppmv', '결로'] },
    });
    this.register({
      id: 'humidity-cal',
      name: '소금물 병으로 습도 센서를 맞춘다',
      icon: '🧂',
      description: '상용 정전용량형 습도 센서(HS1101LF) 데이터시트 곡선과 포화 염 용액 기준 습도(Greenspan 1977)로 개체 편차 오차와 한 점 교정을 보고, 고분자 막 속에 물이 드나드는 3D 장면을 본다',
      component: HumidityCalSim,
      available: true,
      category: 'analysis',
      order: 9.99972,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-16', author: 'Semiconductor Simulator Team',
        features: ['HS1101LF 다항식', '0.31 pF/%RH', '포화 염 11.3 · 32.8 · 52.9 · 75.3 %', '한 점 교정'] },
    });
    // ⑨ RF V/I 프로브(topic.sensor.rfprobe). 엔진 src/physics/rfProbe.js(서고 전용, 반사계수는 rfMatch.js).
    this.register({
      id: 'vi-probe',
      name: 'RF V/I 프로브 — 프로브가 읽는 것',
      icon: '📡',
      description: '부하 · 전력 · 주파수를 바꿔 V/I 프로브가 읽는 전압 · 전류 · 위상과 계산한 전력을 보고, 위상 오차와 패러데이 실드가 전력 측정을 얼마나 바꾸는지 본다. 동축 선로 속 전기장 · 자기장 3D 장면',
      component: VIProbeSim,
      available: true,
      category: 'analysis',
      order: 9.99973,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-16', author: 'Semiconductor Simulator Team',
        features: ['P = ½VI cos φ', '정전 분압 · ωM 고리', '위상 오차 ≈ tan φ · δ', '패러데이 실드'] },
    });
    this.register({
      id: 'icp-transformer',
      name: '플라즈마는 한 바퀴짜리 2차 코일 — ICP 변압기 모형',
      icon: '🌀',
      description: '안테나 코일과 플라즈마를 변압기로 본 등가회로에서 V/I 프로브가 읽는 단자 저항 · 인덕턴스, 거꾸로 구한 플라즈마 저항, 전력 효율을 플라즈마 저항 · 결합 계수로 본다',
      component: IcpTransformerSim,
      available: true,
      category: 'analysis',
      order: 9.99974,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-16', author: 'Semiconductor Simulator Team',
        features: ['Z = R₁ + jωL₁ + ω²M²/(R₂ + jωL₂)', 'η = (R_r − R₁)/R_r', '최대 ωk²L₁/2', '거꾸로 R₂'] },
    });
    // 반도체 물성 기초(topic.semi.physics). 엔진 src/physics/semiPhysics.js(서고 전용, 이동도는 junctionResult.js).
    this.register({
      id: 'fermi-level',
      name: '도핑하면 페르미 준위가 움직인다',
      icon: '🔋',
      description: '실리콘 띠 그림 · 채워진 상태 · 페르미 준위를 도펀트 종류 · 농도 · 온도로 보고(불완전 이온화 포함), 얼어붙음 · 외인성 · 진성 구간과 결정 속 도펀트 캐리어 3D 장면을 본다',
      component: FermiLevelSim,
      available: true,
      category: 'analysis',
      order: 9.99975,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-16', author: 'Semiconductor Simulator Team',
        features: ['n = Nc e^{−(Ec−Ef)/kT}', '전하 중성 · 불완전 이온화', 'ni(T)', 'Si 결정 3D'] },
    });
    this.register({
      id: 'pn-junction',
      name: 'pn 접합 — 공핍층과 정류',
      icon: '⚡',
      description: 'N_A · N_D · 바이어스로 pn 접합의 띠 휨 · 공간 전하 · 전기장 · 공핍층 폭과 전류–전압(정류)을 본다',
      component: PnJunctionSim,
      available: true,
      category: 'analysis',
      order: 9.99976,
      metadata: { version: '1.0.0', lastUpdated: '2026-10-16', author: 'Semiconductor Simulator Team',
        features: ['Vbi = (kT/q)ln(N_AN_D/ni²)', 'W ∝ √(Vbi − V)', 'E_max', 'J = J_s(e^{qV/kT} − 1)'] },
    });

    // 별칭(코스 매트릭스 ID 호환): lithography→lithograph, metallization→metallization-eds-packaging
    this.register({
      id: 'lithography', name: 'Lithography', icon: '💡',
      description: '포토리소그라피 공정 및 PR 코팅',
      component: LithographySimulator, available: true, category: 'process', order: 4
    });
    this.register({
      id: 'metallization', name: '배선·EDS·패키징', icon: '🔌',
      description: '배선·EDS·패키징 통합 공정',
      component: MetallizationEDSPackagingSimulator, available: true, category: 'process', order: 9
    });
    // 반도체 현장 실무영어 (서고 drop-in) — 챕터형 영어 강의 본체. 외부 아티팩트에서 이식.
    this.register({
      id: 'field-english', name: 'Field English', icon: '🗣️',
      description: '현장 실무영어: 장비·로그·DRC/LVS 영문 해석 및 보고',
      component: FieldEnglishSimulator, available: true, category: 'assessment', order: 12
    });
    // 웨이퍼 이송 로봇·센서 (서고 drop-in) — iframe 시뮬 + 챕터별 강의 데크. 외부 아티팩트에서 이식.
    this.register({
      id: 'waferTransfer', name: '웨이퍼 이송', icon: '🤖',
      description: '이송 로봇·센서 Pick & Place 및 인터록 (15주 강의 데크 연동)',
      component: WaferTransferSim, available: true, category: 'equipment', order: 13
    });
    // 「설비 운전」 책 — 데모 운전 실습 둘을 들인다(원본은 데모, HTML 은 scripts/import-equip-trainers.py).
    // 서고에서는 시나리오가 전부 열리고, 판정 결과를 서고 결과 계약(sfab-drill result)으로 받는다.
    this.register({
      id: 'gasDelivery',
      name: '가스 공급계 운전',
      icon: '🧪',
      description: '교재 1권(7장) + MFC · 밸브 · 스크러버 계통 운전 실습 — 시나리오 9개 전부',
      component: GasDeliveryHmi,
      available: true,
      category: 'equipment',
      order: 13.1,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-07',
        author: 'Semiconductor Simulator Team',
        features: ['시나리오 9개 — 절차 1 · 진단 6 · 목표 1 · 누출 대응 1', '가스 라인 기동 절차 채점', '증상만 주고 원인을 찾는 진단', '교재 1권 7장 — 장마다 시나리오 하나와 짝'],
      },
    });
    this.register({
      id: 'etchChamber',
      name: '식각 챔버 운전',
      icon: '⚙️',
      description: 'CCP · ICP 플라즈마 식각 챔버 운전과 시나리오 진단 — 시나리오 8개 전부',
      component: EtchChamberTrainer,
      available: true,
      category: 'equipment',
      order: 13.2,
      metadata: {
        version: '1.0.0',
        lastUpdated: '2026-10-07',
        author: 'Semiconductor Simulator Team',
        features: ['한 화면에서 CCP ↔ ICP 구성 전환', '시나리오 8개 — 절차 1 · 진단 5 · 목표 1 · 대응 1', 'He 백사이드 냉각 상실 대응', '원리·용어 화면 — 노브 → 도면 → 수식'],
      },
    });
    // 직무 드릴은 여기서 등록하지 않는다 — registerJobDrills() 를 서고가 부른다.
  }

  // 시뮬레이터 등록
  register(simulatorConfig) {
    if (!simulatorConfig.id || !simulatorConfig.name) {
      throw new Error('시뮬레이터는 id와 name이 필수입니다.');
    }

    this.simulators.set(simulatorConfig.id, {
      ...simulatorConfig,
      registeredAt: new Date()
    });
  }

  // 특정 시뮬레이터 가져오기
  getSimulator(id) {
    const simulator = this.simulators.get(id);
    return simulator?.available ? simulator.component : null;
  }

  // 시뮬레이터 메타데이터 가져오기
  getSimulatorInfo(id) {
    return this.simulators.get(id);
  }

  // 모든 시뮬레이터 목록 (정렬됨)
  getAllSimulators() {
    return Array.from(this.simulators.values())
      .sort((a, b) => a.order - b.order);
  }

  // 사용 가능한 시뮬레이터만
  getAvailableSimulators() {
    return Array.from(this.simulators.values())
      .filter(sim => sim.available)
      .sort((a, b) => a.order - b.order);
  }

  // 카테고리별 시뮬레이터
  getSimulatorsByCategory(category) {
    return Array.from(this.simulators.values())
      .filter(sim => sim.category === category)
      .sort((a, b) => a.order - b.order);
  }

  // 개발 통계
  getDevStats() {
    const all = Array.from(this.simulators.values());
    return {
      total: all.length,
      available: all.filter(s => s.available).length,
      inDevelopment: all.filter(s => !s.available).length,
      categories: [...new Set(all.map(s => s.category))],
      latestRelease: all
        .filter(s => s.available)
        .sort((a, b) => new Date(b.metadata?.lastUpdated || 0) - new Date(a.metadata?.lastUpdated || 0))[0]
    };
  }
}

// 싱글톤 인스턴스 생성
// ─────────────────────────────────────────────────────────────────────────
// 직무 드릴을 레지스트리에 올린다 — **서고가 부른다.**
//
// 드릴은 서고의 직무 책 안에서 여는 실습이다. 본체는 단일 HTML
// (public/topic-sims/job/*)이고 여기서는 iframe 래퍼 하나를 드릴 수만큼 등록한다.
// 드릴을 추가할 때 이 파일을 고칠 필요는 없다 — jobShelf 의 chapters[].drill.src
// 만 채우면 목록이 따라온다.
//
// **왜 생성자에서 뺐나.** 생성자에서 올리면 이 파일이 jobShelf 를 정적으로 import
// 해야 하고, 그러면 코드 입력 화면 하나 보려는 학생이 직무 책장 데이터까지
// 받는다(jobShelf 43KB + jobDrillGuide 23KB). 게다가 그렇게 올려 둔 64개가
// getAllSimulators() 로 새어 나가, ?legacy=1 자유모드의 **간이시뮬레이터 목록이
// 13개여야 하는데 77개**로 떴다 — 책도 챕터도 없이 제목만 뜨는 항목들이다.
//
// 서고(TopicLibrary / TopicModuleBrowser / MatrixPrototype)는 모두 지연 로드이고
// 이미 jobShelf 를 import 한다. 그 모듈이 뜨는 순간 = 드릴을 열 수 있는 순간이므로,
// 모듈 최상단에서 한 번 부르면 getSimulator(drillSimId) 가 항상 준비돼 있다.
let jobDrillsRegistered = false;
export function registerJobDrills(drills, simIdOf) {
  if (jobDrillsRegistered) return;   // 서고 모듈 여럿이 불러도 한 번만
  jobDrillsRegistered = true;
  drills.forEach((d, i) => simulatorRegistry.register({
    id: simIdOf(d.id), name: d.title, icon: '▶',
    description: `${d.bookTitle} · ${d.chapterTitle}`,
    component: JobDrillSim, available: true, category: 'jobDrill', order: 100 + i,
  }));
}

export const simulatorRegistry = new SimulatorRegistry();

// 개발자 유틸리티 함수들
export const devUtils = {
  // 새 시뮬레이터 추가 헬퍼
  createSimulatorTemplate: (id, name, icon) => ({
    id,
    name,
    icon,
    description: '설명을 입력하세요',
    component: null,
    available: false,
    category: 'process',
    order: 999,
    metadata: {
      version: '0.0.0',
      features: []
    }
  }),

  // 시뮬레이터 상태 변경
  markAsAvailable: (id, component) => {
    const sim = simulatorRegistry.simulators.get(id);
    if (sim) {
      sim.available = true;
      sim.component = component;
      sim.metadata.lastUpdated = new Date().toISOString().split('T')[0];
    }
  },

  // 개발 현황 출력
  logDevProgress: () => {
    const stats = simulatorRegistry.getDevStats();
    console.log('🚀 시뮬레이터 개발 현황');
    console.log(`✅ 완성: ${stats.available}/${stats.total}`);
    console.log(`🔧 개발중: ${stats.inDevelopment}`);
    console.log(`📂 카테고리: ${stats.categories.join(', ')}`);
    if (stats.latestRelease) {
      console.log(`🆕 최신: ${stats.latestRelease.name} (${stats.latestRelease.metadata?.lastUpdated})`);
    }
  }
};
